import { useCallback, useState } from 'react'
import {
  LOFI_PRESETS,
  applyLoFi,
  type BitDepth,
  type LoFiParams,
} from '@/lib/loFi'

/**
 * Lo-Fi colouring panel — exposes the bit-crush + sample-rate
 * downsample + cyclic-stretch pipeline from `@/lib/loFi` with
 * one-tap presets for the classic hardware samplers (S950 /
 * SP-1200 / Amiga) plus manual sliders for each stage.
 *
 * When the user hits "Apply", we bake the params into a new
 * AudioBuffer and hand it back to the Slicer via `onApply`. The
 * Slicer updates its working buffer accordingly so the waveform
 * + slice markers redraw against the coloured version. "Reset"
 * hands back the original buffer so the user can audition
 * clean vs. flavoured without a destructive round-trip.
 */

interface LoFiPanelProps {
  /** Current working buffer — what the Slicer is rendering. May
   *  already be Rubber-Band-warped upstream; we colour on top of
   *  whatever the user's latest baseline is. */
  buffer: AudioBuffer | null
  /** Pristine source buffer so Reset can revert without having
   *  to re-fetch the sample. Passed in from the Slicer's
   *  `originalBufferRef`. */
  originalBuffer: AudioBuffer | null
  /** Fired when the user commits a new flavoured buffer. The
   *  Slicer replaces its working buffer with `next`. */
  onApply: (next: AudioBuffer) => void
  /** Fired when the user resets to the pristine source. */
  onReset: () => void
}

const BIT_DEPTHS: BitDepth[] = [4, 8, 12, 16]
const SR_CHOICES: Array<{ hz: number | null; label: string }> = [
  { hz: 22050, label: '22 k' },
  { hz: 26000, label: '26 k' },
  { hz: 32000, label: '32 k' },
  { hz: null, label: 'native' },
]

export function LoFiPanel({
  buffer,
  originalBuffer,
  onApply,
  onReset,
}: LoFiPanelProps) {
  const [applied, setApplied] = useState<string | null>(null)
  const [working, setWorking] = useState(false)
  const [params, setParams] = useState<LoFiParams>({
    bitDepth: 12,
    sampleRateHz: 32000,
    stretchMode: 'clean',
    cycleMs: 50,
  })

  const loadPreset = useCallback((name: keyof typeof LOFI_PRESETS) => {
    setParams({ ...LOFI_PRESETS[name] })
  }, [])

  const handleApply = useCallback(async () => {
    if (!buffer || working) return
    setWorking(true)
    try {
      // Offline DSP on the main thread. The three stages combined
      // are O(samples × constants) and for our 2 MB / 60 s cap
      // that's a few hundred ms at worst — acceptable for a
      // user-initiated action. If this grows, wrap in a Web Worker.
      const out = applyLoFi(buffer, params)
      onApply(out)
      setApplied(describeParams(params))
    } finally {
      setWorking(false)
    }
  }, [buffer, params, onApply, working])

  const handleReset = useCallback(() => {
    if (!originalBuffer) return
    onReset()
    setApplied(null)
  }, [originalBuffer, onReset])

  return (
    <section
      aria-label="Lo-Fi colouring"
      className="rounded-lg bg-surface-raised border border-brd-subtle"
    >
      <header className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider text-content-tertiary flex items-baseline gap-2">
        <span>Lo-Fi</span>
        <span className="text-[9px] text-content-tertiary/70 normal-case">
          modifies working sample · reset available
        </span>
        {applied && (
          <span className="text-[9px] text-accent normal-case ml-auto tabular-nums">
            {applied}
          </span>
        )}
      </header>
      <div className="px-3 pb-3 flex flex-col gap-2">
        {/* Preset pills — the "I know what I want" path. */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary">
            Preset
          </span>
          <button
            type="button"
            onClick={() => loadPreset('s950')}
            className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent"
            title="Akai S950 — 12-bit / 32 kHz / cyclic stretch"
          >
            S950
          </button>
          <button
            type="button"
            onClick={() => loadPreset('sp1200')}
            className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent"
            title="Akai SP-1200 — 8-bit / 26 kHz / cyclic stretch"
          >
            SP-1200
          </button>
          <button
            type="button"
            onClick={() => loadPreset('amiga')}
            className="px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-[0.14em] text-content-secondary border border-brd-subtle hover:border-[color:var(--accent-halo)] hover:text-accent"
            title="Commodore Amiga Paula — 8-bit / 22 kHz / clean stretch"
          >
            Amiga
          </button>
        </div>

        {/* Bit depth */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-20">
            Bit depth
          </span>
          <div
            role="radiogroup"
            aria-label="Bit depth"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
          >
            {BIT_DEPTHS.map(d => {
              const active = params.bitDepth === d
              return (
                <button
                  key={d}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setParams(p => ({ ...p, bitDepth: d }))}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {d}
                </button>
              )
            })}
          </div>
        </div>

        {/* Sample rate */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-20">
            Sample rate
          </span>
          <div
            role="radiogroup"
            aria-label="Sample rate"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
          >
            {SR_CHOICES.map(c => {
              const active = params.sampleRateHz === c.hz
              return (
                <button
                  key={c.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setParams(p => ({ ...p, sampleRateHz: c.hz }))}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {c.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Stretch mode */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-content-tertiary w-20">
            Stretch
          </span>
          <div
            role="radiogroup"
            aria-label="Stretch mode"
            className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
            title="Clean = no extra stretch (lets Rubber Band do the warping). Cyclic = S950-style OLA with cycle-boundary ringing."
          >
            {(['clean', 'wsola'] as const).map(m => {
              const active = params.stretchMode === m
              return (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setParams(p => ({ ...p, stretchMode: m }))}
                  className={`px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors ${
                    active
                      ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                      : 'text-content-secondary hover:text-content-primary'
                  }`}
                >
                  {m === 'wsola' ? 'Cyclic' : 'Clean'}
                </button>
              )
            })}
          </div>
          {params.stretchMode === 'wsola' && (
            <label className="flex items-center gap-1 text-[10px] font-mono text-content-secondary">
              Cycle
              {/* 40 – 120 ms is the useful S950-cyclic band.
                    Shorter smears; longer reads as a loop. */}
              <input
                type="range"
                min={40}
                max={120}
                step={1}
                value={params.cycleMs}
                onChange={e =>
                  setParams(p => ({
                    ...p,
                    cycleMs: Number(e.target.value),
                  }))
                }
                className="w-24 accent-[color:var(--accent)]"
              />
              <span className="tabular-nums w-10 text-right">
                {params.cycleMs}ms
              </span>
            </label>
          )}
        </div>

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={handleApply}
            disabled={!buffer || working}
            className="btn-primary btn-sm disabled:opacity-50"
          >
            {working ? 'Working…' : 'Apply Lo-Fi'}
          </button>
          {applied && (
            <button
              type="button"
              onClick={handleReset}
              disabled={!originalBuffer}
              className="btn-secondary btn-sm disabled:opacity-50"
            >
              Reset to clean
            </button>
          )}
          <span className="text-[10px] text-content-tertiary ml-auto leading-snug">
            Applies offline. Slice markers stay valid — cyclic stretch extends
            length; bit-crush + SR downsample preserve it.
          </span>
        </div>
      </div>
    </section>
  )
}

function describeParams(p: LoFiParams): string {
  const parts: string[] = []
  parts.push(`${p.bitDepth}-bit`)
  if (p.sampleRateHz !== null)
    parts.push(`${Math.round(p.sampleRateHz / 1000)}k`)
  if (p.stretchMode === 'wsola') parts.push(`cyc ${p.cycleMs}ms`)
  return parts.join(' · ')
}
